import {
  application_cover_inputs_format,
  application_cover_hidden_fields,
  application_beneficiary_types,
  decimalFormatter,
} from '../../constants/application';
import {
  DECLINE_DECISIONS,
  VALID_DECISIONS,
  UW_DECISIONS,
  CONDITION_DECISIONS,
  STANDARD_DECISION,
} from '../../constants/decision';
import { tab_section_sub_tag } from '../../constants/underwriting';
import { numericOnly } from '../common';
import { reset as applicationReset } from '../../features/ApplicationSlice';
import { reset as underwritingReset } from '../../features/UnderwritingSlice';
import { reset as quoteReset } from '../../features/QuoteSlice';
import { reset as decisionReset } from '../../features/DecisionSlice';
import { reset as contactReset } from '../../features/ContactSlice';
import { reset as qiReset } from '../../features/QISlice';

export const filterEnquirySectionsByTag = (sections, tag) => {
  return sections
    .map((section) => ({
      ...section,
      enquiryLines: filterEnquiryLinesByTag(section.enquiryLines, tag),
    }))
    .filter(
      (section) => section.enquiryLines && section.enquiryLines.length > 0
    );
};

export const filterEnquiryLinesByTag = (enquiryLines, tag) =>
  enquiryLines.filter((enquiryLine) => enquiryLine.tags.indexOf(tag) !== -1);

export const filterCoverFields = (cover, hiddenFields = {}) => {
  const allHiddenFields = {
    ...application_cover_hidden_fields,
    ...hiddenFields,
  };
  return Object.keys(cover)
    .sort((a, b) => {
      if (
        a === 'coverType' &&
        (b === 'coverAmount' || b === 'monthlyBenefit')
      ) {
        return -1;
      }
      if (
        (a === 'coverType' || a === 'coverAmount' || a === 'monthlyBenefit') &&
        (b === 'term' ||
          b === 'toAge' ||
          b === 'paymentPeriod' ||
          b === 'deferredPeriod')
      ) {
        return -1;
      }
      if (a === 'deferredPeriod' && b === 'paymentPeriod') {
        return -1;
      }
      return 1;
    })
    .map((name) => ({ name, value: cover[name], type: cover.coverType }))
    .filter((c) => application_cover_inputs_format[c.name] !== undefined)
    .filter((c) => {
      if (
        allHiddenFields[cover.productType] &&
        allHiddenFields[cover.productType].indexOf(c.name) !== -1
      ) {
        return false;
      }
      return true;
    });
};

export const formatCoverValue = (item) => {
  return application_cover_inputs_format[item.name](item.value);
};

export const decimalInputFormatter = (value) => {
  if (value && value !== '' && numericOnly(value)) {
    return decimalFormatter.format(value);
  }
  return '';
};

export const getUnitValidationMessage = (lowerBound, upperBound, label) => {
  return `Please enter a ${label} between ${lowerBound} and ${upperBound}`;
};

export const formatUnitValidation = (question, unit, unitQuestion) => {
  if (unit === 'metric') {
    return {
      error: [
        getUnitValidationMessage(
          `${question.definition.lowerBound}${unitQuestion.units[unit]}`,
          `${question.definition.upperBound}${unitQuestion.units[unit]}`,
          unitQuestion.label
        ),
      ],
    };
  } else {
    try {
      const lowerBound = unitQuestion.convert(question.definition.lowerBound);
      const upperBound = unitQuestion.convert(question.definition.upperBound);
      return {
        error: [
          getUnitValidationMessage(
            Object.keys(unitQuestion.convert(question.definition.lowerBound))
              .map(
                (u, key) => `${lowerBound[u]}${unitQuestion.units[unit][key]}`
              )
              .join(' '),
            Object.keys(unitQuestion.convert(question.definition.lowerBound))
              .map(
                (u, key) => `${upperBound[u]}${unitQuestion.units[unit][key]}`
              )
              .join(' '),
            unitQuestion.label
          ),
        ],
      };
    } catch {
      return question.validationErrors;
    }
  }
};

/**
 * Check if the sub tag exists then use the tag as the
 * name of the accordian
 *
 * @param {Array} tags
 * @param {String} alias
 * @return {String}
 */
export const getSummaryNameFromTag = (tags, alias) => {
  const tag = tags.find((tag) => tag.indexOf(tab_section_sub_tag) === 0);
  if (tag) {
    const replace = new RegExp(`${tab_section_sub_tag}|_`, 'g');
    return tag.replace(replace, ' ').trim();
  }
  return alias;
};

/**
 * Group enquiry lines by tag
 *
 * @param {Array} enquiryLines
 * @return {Object}
 */
export const groupEnquiryLinesByTag = (enquiryLines) => {
  const groupedEnquiryLines = {};

  Object.values(enquiryLines).forEach((enquiryLine) => {
    const name = getSummaryNameFromTag(enquiryLine.tags, enquiryLine.alias);
    if (!groupedEnquiryLines[name]) {
      groupedEnquiryLines[name] = [];
    }
    groupedEnquiryLines[name].push(enquiryLine);
  });
  return groupedEnquiryLines;
};

const sortAlpha = (a, b) => {
  if (a.firstName < b.firstName) return -1;
  if (a.firstName > b.firstName) return 1;
  return 0;
};

export const getAvailableBeneficiaries = (
  applicantId,
  applicants,
  children,
  beneficiaries
) => {
  try {
    return [
      ...applicants
        .filter((applicant) => applicant.id !== applicantId)
        .map((applicant) => ({
          id: applicant.id,
          referenceId: applicant.id,
          firstName: applicant.firstName,
          lastName: applicant.lastName,
          dateOfBirth: applicant.dateOfBirth,
          type: application_beneficiary_types.APPLICANT,
        }))
        .sort(sortAlpha),
      ...children
        .map((child) => ({
          referenceId: child.id,
          ...child,
          type: application_beneficiary_types.CHILD,
        }))
        .sort(sortAlpha),
      ...beneficiaries
        .map((beneficiary) => ({
          referenceId: beneficiary.id,
          ...beneficiary,
          type: application_beneficiary_types.BENEFICIARY,
        }))
        .sort(sortAlpha),
    ];
  } catch {
    return [];
  }
};

/**
 * Check if the product has a declined reason
 *
 * @param {String} decision
 * @returns {Bool}
 */
export const isCoverDeclined = (decision) => {
  if (!decision) return false;
  return (
    DECLINE_DECISIONS.indexOf(decision.replace(/ /g, '').toLowerCase()) !== -1
  );
};

/**
 * Check if the product has a standard decision
 *
 * @param {String} decision
 * @returns {Bool}
 */
export const isCoverStandardDecision = (decision) => {
  if (!decision) return false;
  return decision.replace(/ /g, '').toLowerCase() === STANDARD_DECISION;
};

/**
 * Check if the product has a standard decision
 *
 * @param {String} decision
 * @returns {Bool}
 */
export const isCoverStandard = (decision) => {
  if (!decision) return false;
  return (
    VALID_DECISIONS.indexOf(decision.replace(/ /g, '').toLowerCase()) !== -1
  );
};

/**
 * Check if the product has a standard decision
 *
 * @param {String} decision
 * @param {String} producType
 * @param {Array} conditions
 * @returns {Bool}
 */
export const canShowConditionsTable = (decision, productType, conditions) => {
  if (!decision) return false;
  if (conditions && conditions.length <= 0) return false;
  return (
    CONDITION_DECISIONS.indexOf(decision.replace(/ /g, '').toLowerCase()) !== -1
  );
};

/**
 * Check if the product has a valid decision
 *
 * @param {String} decision
 * @returns {Bool}
 */
export const isValidDecision = (decision) => {
  if (!decision) return false;
  return (
    VALID_DECISIONS.indexOf(decision.replace(/ /g, '').toLowerCase()) !== -1
  );
};

/**
 * Check if the product has a uw decision
 *
 * @param {String} decision
 * @returns {Bool}
 */
export const isUnderwritingDecision = (decision) => {
  if (!decision) return false;
  return UW_DECISIONS.indexOf(decision.replace(/ /g, '').toLowerCase()) !== -1;
};

/**
 * Sort function that sorts
 * conditions array alphabetically
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {Bool}
 */
export const sortConditionsAlpha = (a, b) => {
  if (a.breakdown < b.breakdown) return -1;
  if (a.breakdown > b.breakdown) return 1;
  return 0;
};

/**
 * Remove any null values from an object
 *
 * @param {Object} obj
 * @returns {Object}
 */
export const omitNull = (obj) => {
  Object.keys(obj)
    .filter((k) => obj[k] === null)
    .forEach((k) => delete obj[k]);
  return obj;
};

/**
 * Take an array of conditions containing duplicate
 * em and am ratings and merge them together by
 * breakdown name
 *
 * @param {Array} conditions
 * @returns {Array}
 */
export const mergeConditions = (conditions) => {
  return conditions.reduce((unique, item) => {
    if (unique.findIndex((u) => u.breakdown === item.breakdown) !== -1) {
      return unique.map((u) => {
        if (u.breakdown === item.breakdown) {
          return { ...u, ...omitNull(item) };
        }
        return u;
      });
    } else {
      return [...unique, item];
    }
  }, []);
};

/**
 * Fitler out any applicant decisions with all declined
 * decisions
 *
 * @param {Array} applicantsDecisions
 * @returns {Array}
 */
export const filterApplicantsDecisionsByDeclinedCovers = (
  applicantsDecisions
) => {
  try {
    // Filter out applicants with only declined covers
    return applicantsDecisions.filter(
      (applicant) =>
        applicant.quoteCovers.filter(
          (quoteCover) => !isCoverDeclined(quoteCover.decision)
        ).length > 0
    );
  } catch {
    return applicantsDecisions;
  }
};

/**
 * Return no children if the quote containing riders is declined
 *
 * @param {Array} applicantsDecisions
 * @param {Array} children
 * @returns {Array}
 */
export const filterApplicantsChildrenByDeclinedCovers = (
  applicantsDecisions,
  children
) => {
  try {
    if (
      applicantsDecisions.findIndex(
        (applicant) =>
          applicant.quoteCovers.findIndex(
            (quoteCover) => quoteCover.riders && quoteCover.riders.length > 0
          ) !== -1
      ) === -1
    ) {
      return [];
    }

    if (
      applicantsDecisions.findIndex(
        (applicant) =>
          applicant.quoteCovers.filter(
            (quoteCover) =>
              quoteCover.riders &&
              quoteCover.riders.length > 0 &&
              isCoverDeclined(quoteCover.decision)
          ).length > 0
      ) !== -1
    ) {
      return [];
    }
    return children;
  } catch {
    return children;
  }
};

/**
 * Fitler out any applicants with all declined
 * decisions
 *
 * @param {Array} applicants
 * @param {Array} applicantsDecisions
 * @returns {Array}
 */
export const filterApplicantsByDeclinedCovers = (
  applicants,
  applicantsDecisions
) => {
  try {
    // If there no decisions then just return the applicants
    if (applicantsDecisions.length === 0) return applicants;
    // Filter out applicants with only declined covers
    // Return applicants without declined covers
    return applicants.filter(
      (applicant) =>
        filterApplicantsDecisionsByDeclinedCovers(
          applicantsDecisions
        ).findIndex(
          (applicantDecision) => applicantDecision.id === applicant.id
        ) !== -1
    );
  } catch {
    return applicants;
  }
};

export const formatPostcode = (postcode) => {
  try {
    return postcode
      .replace(/ /g, '')
      .slice(' ', -3)
      .concat(` ${postcode.substring(postcode.length - 3)}`);
  } catch {
    return postcode;
  }
};

export const formatContactData = (data) => {
  if (data.postcode) {
    return { ...data, postcode: formatPostcode(data.postcode) };
  }
  return data;
};

export const resetApplication = (dispatch) => {
  dispatch(applicationReset());
  dispatch(underwritingReset());
  dispatch(quoteReset());
  dispatch(decisionReset());
  dispatch(contactReset());
  dispatch(qiReset());
};
